import { Search, X } from 'lucide-react'
import { motion } from 'motion/react'
import * as React from 'react'

import { usePressMotion } from '../lib/press-motion'
import { cn } from '../lib/utils'
import { TextField, type TextFieldProps } from './text-field'

export interface SearchFieldProps extends Omit<TextFieldProps, 'type' | 'leftIcon'> {
  /**
   * A single-character shortcut that focuses the field from anywhere on the page (e.g. `/`).
   * Installs the window listener AND renders the matching key hint in the trailing slot, so
   * the affordance and the behavior can never drift apart. The hint is pointer-only — touch
   * devices imply no keyboard. Omit for a field with no shortcut.
   */
  shortcut?: string
  /** aria-label for the clear button. A prop (not translated here) to keep this package i18n-agnostic. */
  clearLabel?: string
}

// Don't steal the shortcut while the user is already typing somewhere (same guard shape as
// the undo service's ⌘Z listener).
function isTextEntry(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (typeof ref === 'function') ref(node)
      else if (ref && typeof ref === 'object') (ref as React.MutableRefObject<T | null>).current = node
    }
  }
}

// Clearing has to look like the user did it, so controlled callers get a real onChange with an
// empty value. React de-dupes events against its own cached value, so setting `input.value`
// directly would be swallowed — going through the prototype's setter first updates the node
// without React noticing, and the dispatched `input` event then reads as a genuine edit.
function clearViaNativeSetter(input: HTMLInputElement) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
  setter?.call(input, '')
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

// The app's search input — a TextField in a pill silhouette with the magnifier built in.
// It inherits every TextField behavior (focus ring, validation tones, the text-base→md:text-sm
// iOS zoom guard), and overrides three things the search idiom owns: the full radius, the
// lighter toolbar type treatment, and a fixed 44px height so the field doesn't grow on mobile
// when the font size steps up.
//
// The trailing slot is stateful, because the two affordances are never both useful: an empty
// field advertises the shortcut that fills it, a filled field offers the button that empties it.
// The component owns the whole cycle — the key hint, the window listener behind it, and the
// clear — so a page gets all three from one prop instead of hand-rolling a listener beside a
// decorative <kbd>. Pass `rightIcon` to take the slot over entirely.
const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(
  ({ className, inputClassName, rightIcon, shortcut, clearLabel = 'Clear search', onChange, disabled, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null)
    const press = usePressMotion()

    // Track the value so the trailing slot can swap. Controlled callers are the source of truth;
    // uncontrolled ones are mirrored here (seeded from defaultValue) purely to drive that swap.
    const isControlled = props.value !== undefined
    const [uncontrolledValue, setUncontrolledValue] = React.useState(String(props.defaultValue ?? ''))
    const hasValue = (isControlled ? String(props.value ?? '') : uncontrolledValue).length > 0

    React.useEffect(() => {
      if (!shortcut) return
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== shortcut || event.metaKey || event.ctrlKey || event.altKey) return
        if (isTextEntry(event.target)) return
        event.preventDefault()
        inputRef.current?.focus()
      }
      window.addEventListener('keydown', onKeyDown)
      return () => window.removeEventListener('keydown', onKeyDown)
    }, [shortcut])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setUncontrolledValue(event.target.value)
      onChange?.(event)
    }

    const handleClear = () => {
      const input = inputRef.current
      if (!input) return
      clearViaNativeSetter(input)
      // Clearing is a prelude to retyping, not an exit — keep the caret in the field.
      input.focus()
    }

    return (
      <TextField
        ref={mergeRefs(ref, inputRef)}
        type="search"
        leftIcon={<Search />}
        disabled={disabled}
        onChange={handleChange}
        // `min-h-0` clears TextField's min-h-12 so `h-11` (44px — the touch-target floor) can win.
        className={cn('h-11 min-h-0 rounded-full px-4', className)}
        // Suppress WebKit's built-in search clear — the clear button below replaces it.
        inputClassName={cn('font-light tracking-wide [&::-webkit-search-cancel-button]:appearance-none', inputClassName)}
        {...props}
        rightIcon={
          rightIcon ??
          (hasValue && !disabled ? (
            <motion.button
              type="button"
              {...press}
              onClick={handleClear}
              aria-label={clearLabel}
              // 32px of paint, but a 44px hit area via the pseudo-element — the field is only
              // 44px tall, so a literally-44px button would put an outsized hover circle in it.
              // Focus ring is the app-wide pelorous pairing, but flush — no ring-offset. The
              // offset exists to separate a ring from the surface a control sits on; inside a
              // field there's no such gap to carve, and it only crowds the field's border.
              className="relative -mr-1 flex size-8 items-center justify-center rounded-full text-faint transition-colors before:absolute before:-inset-1.5 before:content-[''] hover:bg-card-sunken hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300">
              <X />
            </motion.button>
          ) : shortcut ? (
            <span
              aria-hidden="true"
              className="pointer-events-none hidden size-6 place-items-center rounded-md border border-border text-xs text-faint sm:grid">
              {shortcut}
            </span>
          ) : null)
        }
      />
    )
  }
)
SearchField.displayName = 'SearchField'

export { SearchField }
